import * as yup from 'yup';
import { ExternalLink, Trash2 } from 'react-feather';
import { useForm } from 'react-form-state-manager';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import keyBy from 'lodash/keyBy';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';

import { FundraisingMode } from '__generated__/graphql';

import { CopyLinkInput } from '../../../../common/Common/CopyLink';
import { Event } from '../Settings/helpers';
import { assetUrl, getServerErrors } from '../../../../common/helpers';
import CharityPicker, { Charity } from '../../../Fundraising/CharityPicker';
import GetEventSettingsQuery from '../Settings/GetEventSettingsQuery';
import UI from '../../../../common/UI';
import config from '../../../config';
import useEditEventMutation from '../Settings/useEditEventMutation';
import useLocale from '../../../../common/useLocale';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useProject from '../../../useProject';
import useQuery from '../../../../api/useQuery';
import useValidation from '../../../../common/useValidation';

export interface FundraisingSettingsPageProps {
  eventId: string;
}

const FundraisingSettingsPage = ({ eventId }: FundraisingSettingsPageProps) => {
  const { t } = useTranslation();
  const project = useProject();

  const { data } = useQuery(GetEventSettingsQuery, {
    variables: {
      project_id: project.id,
      event_id: eventId,
    },
  });

  const event = data?.project.event;

  if (!event) {
    return <UI.PageLoader />;
  }

  return (
    <UI.Section>
      <UI.GridContainer>
        <UI.SectionHeading>
          {t('fundraising')}
        </UI.SectionHeading>

        <FundraisingSettingsForm event={event} />
      </UI.GridContainer>
    </UI.Section>
  );
};

const editFundraisingSettingsSchema = yup.object();

export interface FundraisingSettingsFormProps {
  event: Event;
}

const FundraisingSettingsForm = ({ event }: FundraisingSettingsFormProps) => {
  const { t } = useTranslation();
  const { locale } = useLocale();

  const [keyedCharities, setKeyedCharities] = useState<{ [charityId: string]: Charity; }>(() => ({
    ...event.tickets.reduce((keyedCharities, ticket) => (ticket.charity ? ({
      ...keyedCharities,
      [ticket.charity.id]: ticket.charity,
    }) : keyedCharities), {}),
    ...event.charities.reduce((keyedCharities, charity) => ({
      ...keyedCharities,
      [charity.id]: charity,
    }), {}),
  }));

  const keyedTickets = keyBy(event.tickets, 'id');

  const getValues = (event: Event) => ({
    enable_fundraising: event.enable_fundraising,
    require_fundraising: event.require_fundraising,
    fundraising_mode: event.fundraising_mode,
    allow_other_charity: event.allow_other_charity,
    charities: [
      ...event.charities.map((charity) => charity.id),
      '',
    ],
    charity_tickets: [
      ...event.tickets.filter((ticket) => ticket.charity).map((ticket) => ({
        ticket_id: ticket.id,
        charity_id: ticket.charity.id,
      })),
      { ticket_id: '', charity_id: '' },
    ],
  });

  const form = useForm({
    values: getValues(event),
  });

  const disableFundraising = () => {
    form.set((values) => ({
      ...values,
      enable_fundraising: false,
      require_fundraising: false,
    }));
  };

  const enableFundraising = () => {
    form.set((values) => ({
      ...values,
      enable_fundraising: true,
      require_fundraising: false,
      fundraising_mode: FundraisingMode.after_registration,
    }));
  };

  const requireFundraising = () => {
    form.set((values) => ({
      ...values,
      enable_fundraising: true,
      require_fundraising: true,
      fundraising_mode: FundraisingMode.during_registration,
    }));
  };

  const toggleCharity = (charity: Charity, index: number) => {
    setKeyedCharities((charities) => ({
      ...charities,
      [charity.id]: charity,
    }));

    form.set((values) => {
      const charityIds = [...values.charities];
      charityIds[index] = charity.id;

      if (charityIds[charityIds.length - 1] !== null) {
        charityIds.push(null);
      }

      return {
        ...values,
        charities: uniq(charityIds),
      };
    });
  };

  const deleteCharity = (index: number) => {
    form.delete('charities', index);
  };

  const toggleCharityTicket = (ticket: { id: string; } | null, charity: Charity | null, index: number) => {
    if (charity) {
      setKeyedCharities((charities) => ({
        ...charities,
        [charity.id]: charity,
      }));
    }

    form.set((values) => {
      const charityTickets = [...values.charity_tickets];

      charityTickets[index] = {
        ticket_id: ticket?.id,
        charity_id: charity?.id,
      };

      if (charityTickets[charityTickets.length - 1].ticket_id) {
        charityTickets.push({
          ticket_id: null,
          charity_id: null,
        });
      }

      return {
        ...values,
        charity_tickets: uniqBy(charityTickets, 'ticket_id'),
      };
    });
  };

  const deleteCharityTicket = (index: number) => {
    form.delete('charity_tickets', index);
  };

  const toggleAllowOtherCharity = () => {
    form.set((values) => ({
      ...values,
      allow_other_charity: !values.allow_other_charity,
    }));
  };

  useNavigationPrompt(form.changed());

  const [editEvent, { error, loading }] = useEditEventMutation({
    input: {
      id: event.id,
      fundraising: {
        ...form.values,
        charities: form.values.charities.filter((value) => value),
        charity_tickets: form.values.charity_tickets.filter((value) => value.ticket_id && value.charity_id),
      },
    },
    onSuccess: (event) => {
      form.reset(getValues(event));
    },
  });

  const { valid, errors } = useValidation({
    schema: editFundraisingSettingsSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => editEvent()}>
      <UI.Card sc={{ spacing: 'xl' }}>
        <UI.GridContainer sc={{ columns: ['1fr', '1fr 3fr'], gutter: 1.5 }}>
          <UI.Nav sc={{ pr: [0, 6] }}>
            <UI.Div>
              <UI.H4 sc={{ mb: 1 }}>
                {t('fundraising_by_supporta')}
              </UI.H4>
              <UI.InputDescription sc={{ mb: 1 }}>
                {t('fundraising_description')}
              </UI.InputDescription>
              <UI.InputDescription sc={{ mb: 4 }}>
                <UI.A href={config.supporta.url} target="_blank" rel="noopener noreferrer">
                  {t('about_supporta')}
                  {' '}
                  <UI.Icon>
                    <ExternalLink />
                  </UI.Icon>
                </UI.A>
              </UI.InputDescription>
              <UI.Div>
                <img
                  src={assetUrl('images/logo-supporta.svg')}
                  alt="Supporta"
                  width="100"
                />
              </UI.Div>
            </UI.Div>
          </UI.Nav>

          <UI.FormGrid>
            <UI.ServerErrors error={error} />

            <UI.InputGroup>
              <UI.FormGrid sc={{ gutter: 0.25 }}>
                <UI.Radio
                  checked={form.values.enable_fundraising && !form.values.require_fundraising}
                  onChange={() => enableFundraising()}
                  sc={{ box: true }}
                >
                  <UI.Strong sc={{ block: true }}>
                    {t('allow_fundraising')}
                  </UI.Strong>
                  {t('fundraising_enabled_description')}
                </UI.Radio>
                <UI.Radio
                  checked={form.values.enable_fundraising && form.values.require_fundraising}
                  onChange={() => requireFundraising()}
                  sc={{ box: true }}
                >
                  <UI.Strong sc={{ block: true }}>
                    {t('require_fundraising')}
                  </UI.Strong>
                  {t('fundraising_required_description')}
                </UI.Radio>
                <UI.Radio
                  checked={!form.values.enable_fundraising}
                  onChange={() => disableFundraising()}
                  sc={{ box: true }}
                >
                  <UI.Strong sc={{ block: true }}>
                    {t('disable_fundraising')}
                  </UI.Strong>
                  {t('fundraising_disabled_description')}
                </UI.Radio>
              </UI.FormGrid>
            </UI.InputGroup>

            <UI.HR />

            {form.values.enable_fundraising && !form.values.require_fundraising && (
              <>
                <UI.InputGroup>
                  <UI.InputLabel htmlFor="FundraisingModeQuestion">
                    {t('fundraising_mode_label')}
                  </UI.InputLabel>
                  <UI.Radio
                    {...form.radio('fundraising_mode', FundraisingMode.after_registration)}
                    disabled={form.values.require_fundraising}
                  >
                    {t('fundraising_optional_after_label')}
                  </UI.Radio>
                  <UI.Radio
                    {...form.radio('fundraising_mode', FundraisingMode.during_registration)}
                    disabled={form.values.require_fundraising}
                    id="FundraisingModeQuestion"
                  >
                    {t('fundraising_optional_during_label')}
                  </UI.Radio>
                </UI.InputGroup>
                <UI.Info>
                  {form.values.fundraising_mode === FundraisingMode.during_registration
                    ? t('fundraising_optional_during_description')
                    : t('fundraising_optional_after_description')}
                </UI.Info>
                <UI.HR />
              </>
            )}

            {form.values.enable_fundraising && (
              <>
                <UI.InputGroup
                  sc={{ valid: !errors?.charities, touched: form.touched.charities }}
                >
                  <UI.InputLabel>
                    {t('preferred_charities')}
                  </UI.InputLabel>
                  <UI.InputDescription>
                    {t('preferred_charities_description')}
                    {' '}
                    <UI.A
                      href={config.supporta.signUpUrl[locale]}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('missing_charity')}
                      {' '}
                      <UI.Icon>
                        <ExternalLink />
                      </UI.Icon>
                    </UI.A>
                  </UI.InputDescription>
                  <UI.FormGrid sc={{ gutter: 0.25 }}>
                    {form.values.charities.map((charityId, index) => (
                      <UI.FormGrid sc={{ columns: '1fr 48px', gutter: 0.25 }} key={index}>
                        <UI.Div style={{ zIndex: 400 - index }}>
                          <CharityPicker
                            charity={keyedCharities[charityId]}
                            onChange={(charity) => toggleCharity(charity, index)}
                            filters={{ event_id: event.id }}
                            nullable={false}
                          />
                        </UI.Div>
                        {index < form.values.charities.length - 1 && (
                          <UI.Div>
                            <UI.Button
                              sc={{ blank: true, color: 'gray.400' }}
                              onClick={() => deleteCharity(index)}
                              aria-label={t('remove')}
                            >
                              <UI.Icon>
                                <Trash2 />
                              </UI.Icon>
                            </UI.Button>
                          </UI.Div>
                        )}
                      </UI.FormGrid>
                    ))}
                  </UI.FormGrid>
                  <UI.ErrorMessages attribute={t('charities')} errors={errors?.charities} />
                </UI.InputGroup>

                <UI.InputGroup>
                  <UI.Checkbox
                    checked={!form.values.allow_other_charity && form.values.charities.length > 0}
                    onChange={() => toggleAllowOtherCharity()}
                    disabled={form.values.charities.length === 0}
                  >
                    {t('make_charities_exclusive')}
                  </UI.Checkbox>
                </UI.InputGroup>
              </>
            )}

            <UI.InputGroup>
              <UI.InputLabel>
                {t('charity_tickets')}
              </UI.InputLabel>
              <UI.InputDescription sc={{ mb: 1 }}>
                {t('charity_tickets_description')}
              </UI.InputDescription>
              <UI.FormGrid sc={{ gutter: 0.25 }}>
                {form.values.charity_tickets.map((value, index) => (
                  <UI.FormGrid sc={{ columns: '1fr 1fr 48px', gutter: 0.25 }} key={index}>
                    <UI.Div>
                      <UI.Select
                        value={value.ticket_id || ''}
                        onChange={(event) => toggleCharityTicket(
                          keyedTickets[event.target.value],
                          keyedCharities[value.charity_id],
                          index,
                        )}
                        sc={{ placeholder: !value.ticket_id }}
                      >
                        <option value="">{t('choose_ticket')}</option>
                        {event.tickets.map((ticket) => (
                          <option value={ticket.id} key={ticket.id}>
                            {ticket.title}
                          </option>
                        ))}
                      </UI.Select>
                    </UI.Div>
                    <UI.Div style={{ zIndex: 200 - index }}>
                      <CharityPicker
                        charity={keyedCharities[value.charity_id]}
                        onChange={(charity) => toggleCharityTicket(keyedTickets[value.ticket_id], charity, index)}
                        filters={{ event_id: event.id }}
                        nullable={false}
                      />
                    </UI.Div>
                    {index < form.values.charity_tickets.length - 1 && (
                    <UI.Div>
                      <UI.Button
                        sc={{ blank: true, color: 'gray.400' }}
                        onClick={() => deleteCharityTicket(index)}
                        aria-label={t('remove')}
                      >
                        <UI.Icon>
                          <Trash2 />
                        </UI.Icon>
                      </UI.Button>
                    </UI.Div>
                    )}
                  </UI.FormGrid>
                ))}
              </UI.FormGrid>
            </UI.InputGroup>

            <UI.HR />

            {!form.values.require_fundraising && (
              <>
                <UI.Div>
                  <UI.FlexContainer sc={{ mb: 2, justifyContent: 'space-between' }}>
                    <UI.InputLabel sc={{ mb: 0 }}>
                      {t('supporta_link')}
                    </UI.InputLabel>
                    <UI.Span sc={{ mt: 0 }}>
                      <UI.A href={event.supporta_url} target="_blank" rel="noopener noreferrer">
                        {t('open')}
                        {' '}
                        <UI.Icon>
                          <ExternalLink />
                        </UI.Icon>
                      </UI.A>
                    </UI.Span>
                  </UI.FlexContainer>
                  <CopyLinkInput value={event.supporta_url} />
                  <UI.InputDescription sc={{ mt: 1 }}>
                    {t('supporta_link_description')}
                  </UI.InputDescription>
                </UI.Div>

                <UI.HR />
              </>
            )}

            <UI.FormButtons
              form={form}
              submitLabel={t('save_event')}
              loading={loading}
              valid={valid}
            />
          </UI.FormGrid>
        </UI.GridContainer>
      </UI.Card>
    </UI.Form>
  );
};

export default FundraisingSettingsPage;
